import Pdf from "../Pdf";

import Patient from "../Crud/Patient";
import DiagnosisCode from "../Crud/DiagnosisCode";

/**
 * Good Faith Estimate PDF: estimated charges and patient responsibility for an order.
 */
export default class PdfGoodFaithEstimate extends Pdf {
    /**
     * Constructs a new PdfGoodFaithEstimate instance.
     * @param {Object} data - Data for the document, including chartContext and rows.
     * @param {Object} options - Options passed to the base Pdf constructor.
     */
    constructor(data = {}, options = {}) {
        super(data, options);
    }

    /**
     * Renders the PDF document.
     */
    async render() {
        await this.fontLoadPromise;

        const chartContext = this.data.chartContext || {};
        const patient = chartContext.chart.patient[chartContext.patientId] || {};
        const order = chartContext.chart.orderActivity[chartContext.orderId] || {};
 
        const orderActivityApprovals = Object.values(
            chartContext.chart.orderActivityApprovals || {}
        ).find((orderActivityApproval) => {
            return orderActivityApproval.orderActivityId === chartContext.orderId;
        });
        const approval = Object.values(chartContext.chart?.approval || {}).find((approvalRecord) => {
            return approvalRecord?.id === orderActivityApprovals?.approvalsId;
        });
        const mainContext = this.data.mainContext || {};
        const salesLocationId = order?.salesLocationId || order?.serviceLocationId || null;
        const location = salesLocationId
            ? mainContext?.locationsIndexed?.[salesLocationId]
            : null;

        const rows = this.data.rows || [];

        let y = this.renderHeader({
            title: !order?.insuranceTypeId || order?.insuranceTypeId === 4 ? "Good Faith Estimate" : "Estimate", // If no insurnace or insurance type is Cash, show "Good Faith Estimate", otherwise show "Estimate"
        });
        y += 52;


        const patientResponsibilityEstimate =
            this.computeGoodFaithEstimatePatientResponsibility(approval, rows, order);
 
        y = this.renderGoodFaithEstimatePatientAndLocationSection(
            y,
            patient,
            order,
            location,
            chartContext,
            mainContext
        );
        y += 36;

        y = this.renderGoodFaithEstimateChargesSection(
            y,
            rows,
            patientResponsibilityEstimate,
            order
        );

        /**
         * If the order is not a cash order, render the patient responsibility section.
         */
        if (order?.insuranceTypeId !== 4) {
            y = this.renderGoodFaithEstimatePatientResponsibilitySection(
                y,
                patientResponsibilityEstimate,
            );
        }

        y = this.renderGoodFaithEstimateDisclaimerAtPageFooter(y, order);

        this.renderPageNumbers();
    }

    /**
     * Formats a number as United States dollars for the Good Faith Estimate.
     * @param {unknown} value - Raw numeric or string amount.
     * @returns {string} Formatted currency string.
     */
    formatCurrencyValueForGoodFaithEstimate(value) {
        const numberValue = Number(value);
        if (Number.isNaN(numberValue)) {
            return "$0.00";
        }
        return numberValue.toLocaleString("en-US", { style: "currency", currency: "USD" });
    }

    /**
     * Builds a single address line for city, state, and postal code.
     * @param {string|null|undefined} city - City name.
     * @param {string|null|undefined} state - State code or name.
     * @param {string|null|undefined} postalCode - Postal or ZIP code.
     * @returns {string} Combined city, state, and postal line.
     */
    formatCityStateZipLine(city, state, postalCode) {
        const cityState = [city, state].filter(Boolean).join(", ").trim();
        if (cityState && postalCode) {
            return `${cityState} ${postalCode}`;
        }
        return cityState || postalCode || "";
    }

    /**
     * Computes deductible, coinsurance, and patient responsibility (mirrors
     * DialogPatientResponsibilityEstimator logic for copay derivation from approval when needed).
     * @param {Object} approval - Approval object from chart context.
     * @param {Array} rows - Array of product line rows from data.
     * @returns {Object} totalCharges, deductible, coinsurance, patientResponsibility, copayPercentage.
     */
    computeGoodFaithEstimatePatientResponsibility(approval, rows, order) {
        const rawTotalCharges = rows.reduce((sum, productLineRow) => {
            return sum + (productLineRow.amount || 0) * (productLineRow.qty || 0);
        }, 0);
        const deductibleValue = Number.parseFloat(approval?.deductible) || 0;
        const baseChargesAfterDeductible = Math.max(rawTotalCharges - deductibleValue, 0);
        const approvalCoinsurance = Number(approval?.coInsurance);

        let copayPercentage = Number(this.data?.copayPercentage);
        if (!Number.isFinite(copayPercentage)) {
            if (baseChargesAfterDeductible > 0 && Number.isFinite(approvalCoinsurance)) {
                copayPercentage = (approvalCoinsurance / baseChargesAfterDeductible) * 100;
            } else {
                copayPercentage = 20;
            }
        }

        const totalCharges = rows.reduce((sum, productLineRow) => {
            return sum + ((productLineRow?.amount || 0) * (productLineRow.qty || 0));
        }, 0);
        const deductible = Number.parseFloat(String(deductibleValue)) || 0;
        const coinsurance = Math.max(totalCharges - deductible, 0) * (copayPercentage / 100);
        const rawPatientResponsibility = deductible + coinsurance;

        return {
            totalCharges: Number(totalCharges.toFixed(2)),
            deductible: Number(deductible.toFixed(2)),
            coinsurance: Number(coinsurance.toFixed(2)),
            patientResponsibility: Number(
                order?.insuranceTypeId === 4
                    ? totalCharges.toFixed(2)
                    : Math.min(rawPatientResponsibility, totalCharges).toFixed(2)
            ),
            copayPercentage,
        };
    }

    /**
     * Renders patient name, location, order identifier, addresses, and current date.
     * @param {number} y - Starting vertical position.
     * @param {Object} patient - Patient object from chart context.
     * @param {Object} order - Order object from chart context.
     * @param {Object} location - Location object from chart context.
     * @param {Object} chartContext - Chart context object.
     * @param {Object} mainContext - Main context object.
     * @returns {number} Vertical position after the block.
     */
    renderGoodFaithEstimatePatientAndLocationSection(y, patient, order, location, chartContext, mainContext) {
        function getDiagnosisCodeKeys(order) {
            return Object.keys(order).filter(orderKey => /DiagnosisCodeId$/i.test(orderKey));
        }

        const column1X = this.margin.left;
        const column2X = this.margin.left + 205;
        const column3RightX = this.document.internal.pageSize.width - this.margin.right;

        const patientDisplayName = Patient.getDisplayName(patient) || "Patient Name";
 
        const patientDateOfBirth = patient?.dateOfBirth 
            ? new Date(patient.dateOfBirth).toLocaleDateString("en-US")
            : "";

        const locationDisplayName = location
            ? `Rehab Medical of ${location?.name}`
            : "Rehab Medical";
        const patientStreetLine = (Array.isArray(patient?.address) ? patient.address : [patient?.address])
            .filter(Boolean)
            .join(" ")
            .trim();
        const locationStreetLine = (Array.isArray(location?.address) ? location.address : [location?.address])
            .filter(Boolean)
            .join(" ")
            .trim();
        const patientCityStateZipLine = this.formatCityStateZipLine(
            patient.city || null,
            patient.state || null,
            patient.zipCode || null
        );
        const locationCityStateZipLine = this.formatCityStateZipLine(
            location?.city || null,
            location?.state || null,
            location?.zipCode || null
        );
        const currentDateString = new Date().toLocaleDateString("en-US");

        const diagnosisCodeKeys = getDiagnosisCodeKeys(order);
        const diagnosisCodes = diagnosisCodeKeys
            .map(orderKey => {
                const diagnosisCode = chartContext.chart.diagnosisCode[chartContext.chart.orderActivity[chartContext.orderId]?.[orderKey]];
                return diagnosisCode ? DiagnosisCode.getDisplayName(diagnosisCode) : null;
            })
            .filter(diagnosisCodeDisplayName => diagnosisCodeDisplayName !== null)
            .join(", ");

            
        // If the location does not have a National Provider Identifier, get the National Provider Identifier from the billing location
        let nationalProviderIdentifier = location?.npi;
        if (!nationalProviderIdentifier) {
            const billingLocationId = order?.billingLocationId;
            const billingLocation = billingLocationId
                ? mainContext?.locationsIndexed?.[billingLocationId]
                : null;
            nationalProviderIdentifier = billingLocation?.npi;
        }

        const lineHeight = 12;
        const sectionSpacing = 18;

        // Patient Name, Location Name, Order Number
        this.saveSettings();
        this.document.setFontSize(11);
        this.document.setFont(undefined, "bold");
        this.document.text(patientDisplayName, column1X, y);
        this.document.text(locationDisplayName, column2X, y);
        this.document.text(`Order #${order.id}`, column3RightX, y, { align: "right" });
        this.restoreSettings();

        y += lineHeight;

        // Patient Address, Location Address, Current Date
        this.saveSettings();
        this.document.setFontSize(10);
        this.document.setFont(undefined, "normal");
        this.document.text(patientStreetLine, column1X, y);
        this.document.text(locationStreetLine, column2X, y);
        this.document.text(currentDateString, column3RightX, y, { align: "right" });
        this.restoreSettings();

        y += lineHeight;

        // Patient City/State/Zip, Location City/State/Zip
        this.saveSettings();
        this.document.setFontSize(10);
        this.document.setFont(undefined, "normal");
        this.document.text(patientCityStateZipLine, column1X, y);
        this.document.text(locationCityStateZipLine, column2X, y);
        this.restoreSettings();

        y += sectionSpacing;

        // Date of Birth Section, National Provider Identifier Section, and Diagnosis Codes Section
        const hasDateOfBirth = Boolean(patientDateOfBirth);
        const hasNationalProviderIdentifier = Boolean(nationalProviderIdentifier);
        const hasDiagnosisCodes = Boolean(diagnosisCodes);

        if (hasDateOfBirth || hasNationalProviderIdentifier || hasDiagnosisCodes) {
            this.saveSettings();
            this.document.setFontSize(10);
            this.document.setFont(undefined, "bold");
            if (hasDateOfBirth) {
                this.document.text("DOB", column1X, y);
            }
            if (hasNationalProviderIdentifier) {
                this.document.text("NPI", column2X, y);
            }
            if (hasDiagnosisCodes) {
                this.document.text("Diagnosis Codes", column3RightX, y, { align: "right" });
            }
            this.restoreSettings();

            y += lineHeight;

            this.saveSettings();
            this.document.setFontSize(10);
            this.document.setFont(undefined, "normal");
            if (hasDateOfBirth) {
                this.document.text(patientDateOfBirth, column1X, y);
            }
            if (hasNationalProviderIdentifier) {
                this.document.text(nationalProviderIdentifier, column2X, y);
            }
            if (hasDiagnosisCodes) {
                this.document.text(diagnosisCodes, column3RightX, y, { align: "right" });
            }
            this.restoreSettings();

            y += sectionSpacing;
        }

        return y;
    }

    /**
     * Renders the charges table and total charges line.
     * @param {number} y - Starting vertical position.
     * @param {Array} rows - Order line rows with amounts and quantities.
     * @param {Object} patientResponsibilityEstimate - Computed totals.
     * @param {Object} order - Order object from chart context.
     * @returns {number} Vertical position after the section.
     */
    renderGoodFaithEstimateChargesSection(
        y,
        rows,
        patientResponsibilityEstimate,
        order
    ) {
        const headerTitle = "Good Faith Estimate";

        y = this.renderAdditionalPageIfNeeded(
            y,
            { renderHeader: true, title: headerTitle },
            180
        );

        this.renderSectionTitle("Charges", null, y);
        y += 16;

        y = this.renderText("", "These estimated charges reflect the items and quantities currently included on your order.", this.margin.left, y, null, { fontSize: 10 });

        const formatCurrency = (value) => this.formatCurrencyValueForGoodFaithEstimate(value);

        const chargeTableRows = rows.map((row) => {
            const healthcareCommonProcedureCode = `${row.hcpcs?.code || ""}${row.modifierCode?.code ? ` ${row.modifierCode.code}` : ""
                }`
                .trim() || "N/A";
            const quantity = Number(row.qty || 0);
            const unitAmount = Number(row?.amount || 0);
            const rowTotal = quantity * unitAmount;
            return [
                healthcareCommonProcedureCode,
                row?.productSubTypeName || "",
                String(quantity),
                formatCurrency(unitAmount),
                formatCurrency(rowTotal),
            ];
        });

        y = this.renderTable({
            columns: [
                { label: "HCPCS", columnWidth: 90 },
                { label: "Product Name", columnWidth: 260 },
                { label: "Qty", columnWidth: 50, align: "right" },
                { label: "Unit Amount", columnWidth: 90, align: "right" },
                { label: "Total", columnWidth: 90, align: "right" },
            ],
            rows:
                chargeTableRows.length > 0
                    ? chargeTableRows
                    : [["N/A", "No billable products", "0", "$0.00", "$0.00"]],
            x: this.margin.left,
            y,
            renderHeader: true,
            title: headerTitle,
        });

        this.saveSettings();
        this.document.setFont(undefined, "bold");
        this.document.setFontSize(10);
        y += 14;
        this.document.text(order?.insuranceTypeId !== 4 ? "Total Charges" : "Estimated Patient Responsibility", this.margin.left, y);
        const totalsRightX = this.document.internal.pageSize.width - this.margin.right - 4;
        this.document.text(
            formatCurrency(patientResponsibilityEstimate.totalCharges),
            totalsRightX,
            y,
            { align: "right" }
        );
        this.restoreSettings();

        return y;
    }

    /**
     * Renders patient responsibility breakdown and highlighted total.
     * @param {number} y - Starting vertical position.
     * @param {Object} patientResponsibilityEstimate - Computed responsibility amounts.
     * @returns {number} Vertical position after the section.
     */
    renderGoodFaithEstimatePatientResponsibilitySection(
        y,
        patientResponsibilityEstimate
    ) {
        const headerTitle = "Good Faith Estimate";
        const valueColumnX = this.document.internal.pageSize.width - this.margin.right - 4;
        const formatCurrency = (value) => this.formatCurrencyValueForGoodFaithEstimate(value);

        y += 36;

        y = this.renderAdditionalPageIfNeeded(
            y,
            { renderHeader: true, title: headerTitle },
            200
        );

        this.renderSectionTitle("Patient Responsibility", null, y);
        y += 16;


        y = this.renderText("", "These are your estimated out-of-pocket costs based on any insurance benefits or discounts you may be eligible for.", this.margin.left, y, null, {
            fontSize: 10,
            labelFontStyle: "normal",
            valueFontStyle: "normal",
            renderHeader: true,
            title: headerTitle,
        });
        y += 12;

        y = this.renderGoodFaithEstimateLabeledCurrencyRow(
            y,
            "Remaining Deductible",
            patientResponsibilityEstimate.deductible,
            formatCurrency,
            valueColumnX,
            { rowSpacing: 18 }
        );
        y = this.renderGoodFaithEstimateLabeledCurrencyRow(
            y,
            "Coinsurance",
            patientResponsibilityEstimate.coinsurance,
            formatCurrency,
            valueColumnX,
            { rowSpacing: 12 }
        );


        const totalAmountText = formatCurrency(patientResponsibilityEstimate.patientResponsibility);
        const totalRowTop = y;
        const totalRowHeight = 20;
        const totalTextVerticalOffset = totalRowTop + 4;
        const totalBoxX = this.margin.left - 4;
        const totalBoxWidth = valueColumnX + 4 - totalBoxX;

        this.saveSettings();
        this.document.setDrawColor(0, 0, 0);
        this.document.setLineWidth(1);
        this.document.rect(totalBoxX, totalRowTop, totalBoxWidth, totalRowHeight);
        this.document.setFont(undefined, "bold");
        this.document.setFontSize(10);
        this.document.text("Estimated Patient Responsibility", this.margin.left, totalTextVerticalOffset, {
            baseline: "top",
        });
        this.document.text(totalAmountText, valueColumnX, totalTextVerticalOffset, {
            align: "right",
            baseline: "top",
        });
        this.restoreSettings();

        return totalRowTop + totalRowHeight;
    }

    /**
     * Renders one label with a dotted leader and right-aligned currency amount.
     * @param {number} y - Baseline vertical position for the row.
     * @param {string} labelText - Left column label.
     * @param {number} amount - Numeric amount for display.
     * @param {function(number): string} formatCurrency - Formatter for the amount.
     * @param {number} valueColumnX - Right edge X for the amount.
     * @param {Object} rowOptions - Optional rowSpacing.
     * @returns {number} Vertical position after the row.
     */
    renderGoodFaithEstimateLabeledCurrencyRow(
        y,
        labelText,
        amount,
        formatCurrency,
        valueColumnX,
        rowOptions = {}
    ) {
        const rowSpacing = rowOptions.rowSpacing ?? 18;
        const formattedAmount = formatCurrency(amount);

        this.saveSettings();
        this.document.setFont(undefined, "normal");
        this.document.setFontSize(10);
        this.document.text(labelText, this.margin.left, y);

        const labelWidth = this.document.getTextWidth(labelText);
        const lineStartX = this.margin.left + labelWidth + 8;
        const lineEndX = valueColumnX - this.document.getTextWidth(formattedAmount) - 10;

        if (lineEndX > lineStartX + 6) {
            this.document.setDrawColor(150, 150, 150);
            this.document.setLineWidth(0.6);
            this.document.setLineDashPattern([1.2, 1.8], 0);
            this.document.line(lineStartX, y - 1, lineEndX, y - 1);
            this.document.setLineDashPattern([], 0);
        }

        this.document.text(formattedAmount, valueColumnX, y, { align: "right" });
        this.restoreSettings();

        y += rowSpacing;
        return y;
    }

    /**
     * Adds a page if needed, then renders the footer disclaimer.
     * @param {number} y - Current content bottom position.
     * @returns {number} Vertical position after disclaimer placement logic.
     */
    renderGoodFaithEstimateDisclaimerAtPageFooter(verticalPosition, order) {
        const y = this.document.internal.pageSize.height - this.margin.bottom - 70;
 
        if (verticalPosition > y - 30) {
            this.document.addPage();
        }

        const disclaimerText = !order?.insuranceTypeId || order?.insuranceTypeId === 4 ?
            "This Good Faith Estimate is not a bill and not a contract. It does not require you to obtain items or services from Rehab Medical or any provider listed in this estimate.\n\n" +
            "This estimate reflects only the items and services reasonably expected to be furnished based on information available at the time it was issued. Actual items, services, or charges may differ based on changes to your medical needs or ordered items and services. There may also be additional items or services your provider recommends as part of your course of care that are not reflected here and would require separate scheduling or a separate estimate."
            : 
            "This cost estimate is not a bill and not a contract. It does not require you to obtain items or services from Rehab Medical or any provider listed in this estimate.\n\n" +
            "This estimate reflects only the items and services reasonably expected to be furnished based on information available at the time it was issued. Your actual out-of-pocket costs may differ based on your insurance plan's final determination of benefits, changes to your coverage, deductible balance at time of service, or changes to your ordered items and services. Please contact your insurance plan for a more detailed explanation of your benefits.";
        
        const disclaimerMaximumWidth = this.document.internal.pageSize.width - this.margin.left - this.margin.right;

        this.saveSettings();
        this.document.setFontSize(10);
        this.document.setFont(undefined, "normal");
        
        const wrappedDisclaimerLines = this.wrapText(
            disclaimerText,
            disclaimerMaximumWidth,
            {
                font: this.document.getFont().fontName,
                fontSize: 10,
                fontStyle: "normal"
            }
        );

        let currentDisclaimerY = y;
        wrappedDisclaimerLines.forEach((line) => {
            this.document.text(line, this.margin.left, currentDisclaimerY);
            currentDisclaimerY += 12;
        });

        this.restoreSettings();

        return y;
    }
}
